import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useCore } from '../hooks/useCore';
import { useMatrix } from '../hooks/useMatrix';
import Loader from '../components/Loader';
import { PACKAGE_LABELS, PACKAGE_NAMES, PACKAGE_COLORS, formatDate } from '../utils/helpers';
import '../styles/matrix.css';

export default function Matrix({ onNotify }) {
  const { account, userId, provider } = useWeb3();
  const { getUserInfo } = useCore();
  const { getUserBoard, getBoardCycleCount, getBoardCycleHistory } = useMatrix();

  const [selectedPkg, setSelectedPkg] = useState(0);
  const [board, setBoard] = useState(null);
  const [cycles, setCycles] = useState('0');
  const [cycleHistory, setCycleHistory] = useState([]);
  const [userPackages, setUserPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPkg, setHistoryPkg] = useState(null);

  useEffect(() => {
    if (provider && userId) {
      loadUserPackages();
    }
    // eslint-disable-next-line
  }, [userId, provider]);

  useEffect(() => {
    if (provider && userId) {
      loadBoard();
    }
    // eslint-disable-next-line
  }, [userId, selectedPkg, provider]);

  async function loadUserPackages() {
    try {
      const info = await getUserInfo(userId);
      if (info) setUserPackages(info.packageActive);
    } catch {}
  }

  async function loadBoard() {
    if (!userId) return;
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        getUserBoard(userId, selectedPkg),
        getBoardCycleCount(userId, selectedPkg),
      ]);
      setBoard(b);
      setCycles(c);
    } catch {}
    setLoading(false);
  }

  async function loadHistory(pkgId) {
    setHistoryPkg(pkgId);
    setShowHistory(true);
    try {
      const h = await getBoardCycleHistory(userId, pkgId);
      setCycleHistory(h.reverse()); // newest first
    } catch {
      setCycleHistory([]);
    }
  }

  if (!account) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <div className="connect-icon">&#x1F512;</div>
          <h2>Matrix Board</h2>
          <p>Connect your wallet to view boards</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <h2>Not Registered</h2>
          <p>Go to Dashboard to register first</p>
        </div>
      </div>
    );
  }

  const l1Filled = board ? board.firstLevel.filter(x => x !== '0').length : 0;
  const l2Filled = board ? board.secondLevel.filter(x => x !== '0').length : 0;
  const totalSlots = 6;
  const filledSlots = l1Filled + l2Filled;
  const progressPct = (filledSlots / totalSlots) * 100;

  // Cycle history modal
  if (showHistory) {
    return (
      <div className="page-container">
        <div className="history-header">
          <button className="back-btn" onClick={() => setShowHistory(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <h2 className="page-title">{PACKAGE_NAMES[historyPkg]} History</h2>
        </div>

        {cycleHistory.length > 0 ? (
          <div className="history-list">
            {cycleHistory.map((cycle, idx) => (
              <div key={idx} className="history-card glass-card animate-in" style={{animationDelay:`${idx*0.05}s`}}>
                <div className="history-card-header">
                  <span className="history-cycle-badge">Cycle #{cycle.cycleNumber}</span>
                  <span className="history-date">{formatDate(cycle.completedAt)}</span>
                </div>
                {/* Mini board visualization */}
                <div className="mini-board">
                  <div className="mini-level">
                    {cycle.firstLevelUsers.map((id, i) => (
                      <div key={i} className={`mini-node ${id !== '0' ? 'filled' : 'empty'}`}>
                        {id !== '0' ? id : '--'}
                      </div>
                    ))}
                  </div>
                  <div className="mini-connector"></div>
                  <div className="mini-level mini-l2">
                    {cycle.secondLevelUsers.map((id, i) => (
                      <div key={i} className={`mini-node mini-sm ${id !== '0' ? 'filled' : 'empty'}`}>
                        {id !== '0' ? id : '--'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No completed cycles yet</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page-container">
      <h2 className="page-title">Matrix Board</h2>

      {/* Package selector - card style */}
      <div className="matrix-pkg-grid animate-in">
        {PACKAGE_NAMES.map((name, i) => {
          const isActive = userPackages[i];
          const isSelected = selectedPkg === i;
          return (
            <button
              key={i}
              className={`matrix-pkg-card ${isSelected ? 'selected' : ''} ${isActive ? 'active' : 'locked'}`}
              onClick={() => isActive && setSelectedPkg(i)}
              disabled={!isActive}
            >
              <span className="mpkg-color" style={{background: isActive ? PACKAGE_COLORS[i] : 'var(--text-muted)'}}></span>
              <span className="mpkg-name">{name}</span>
              <span className="mpkg-price">{PACKAGE_LABELS[i]}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <Loader text="Loading board..." />
      ) : board ? (
        <>
          {/* Board card */}
          <div className="board-card glass-card animate-in" style={{animationDelay:'0.1s'}}>
            {/* Header with stats */}
            <div className="board-top-row">
              <div className="board-pkg-badge" style={{borderColor: PACKAGE_COLORS[selectedPkg]}}>
                <span style={{color: PACKAGE_COLORS[selectedPkg]}}>{PACKAGE_NAMES[selectedPkg]}</span>
              </div>
              <span className={`board-status-pill ${board.blocked ? 'blocked' : 'active'}`}>
                {board.blocked ? 'Blocked' : 'Active'}
              </span>
            </div>

            {/* Progress bar */}
            <div className="board-progress-wrap">
              <div className="board-progress-bar">
                <div className="board-progress-fill" style={{width: `${progressPct}%`}}></div>
              </div>
              <span className="board-progress-text">{filledSlots}/{totalSlots} Slots Filled</span>
            </div>

            {/* X6 Matrix Visualization */}
            <div className="matrix-viz">
              {/* Root */}
              <div className="viz-level viz-root">
                <div className="viz-node root-node">
                  <span className="viz-node-id">{userId}</span>
                  <span className="viz-node-label">You</span>
                </div>
              </div>

              {/* Connectors L0 -> L1 */}
              <div className="viz-branches">
                <svg className="viz-svg" viewBox="0 0 200 30" preserveAspectRatio="none">
                  <path d="M100,0 L50,30" stroke="url(#grad1)" strokeWidth="2" fill="none" opacity="0.5"/>
                  <path d="M100,0 L150,30" stroke="url(#grad1)" strokeWidth="2" fill="none" opacity="0.5"/>
                  <defs>
                    <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#ec4899" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Level 1 */}
              <div className="viz-level viz-l1">
                {board.firstLevel.map((id, i) => (
                  <div key={i} className={`viz-node ${id !== '0' ? 'viz-filled' : 'viz-empty'}`}>
                    <span className="viz-node-id">{id !== '0' ? id : ''}</span>
                    {id === '0' && <span className="viz-node-empty-icon">+</span>}
                  </div>
                ))}
              </div>

              {/* Connectors L1 -> L2 */}
              <div className="viz-branches wide">
                <svg className="viz-svg" viewBox="0 0 300 24" preserveAspectRatio="none">
                  <path d="M75,0 L37,24" stroke="url(#grad1)" strokeWidth="1.5" fill="none" opacity="0.4"/>
                  <path d="M75,0 L112,24" stroke="url(#grad1)" strokeWidth="1.5" fill="none" opacity="0.4"/>
                  <path d="M225,0 L187,24" stroke="url(#grad1)" strokeWidth="1.5" fill="none" opacity="0.4"/>
                  <path d="M225,0 L262,24" stroke="url(#grad1)" strokeWidth="1.5" fill="none" opacity="0.4"/>
                </svg>
              </div>

              {/* Level 2 */}
              <div className="viz-level viz-l2">
                {board.secondLevel.map((id, i) => (
                  <div key={i} className={`viz-node viz-sm ${id !== '0' ? 'viz-filled' : 'viz-empty'}`}>
                    <span className="viz-node-id">{id !== '0' ? id : ''}</span>
                    {id === '0' && <span className="viz-node-empty-icon">+</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Board stats */}
            <div className="board-stats-row">
              <div className="board-stat-item">
                <span className="bstat-value">{board.currentReferrer !== '0' ? '#'+board.currentReferrer : 'Top'}</span>
                <span className="bstat-label">Upline</span>
              </div>
              <div className="board-stat-divider"></div>
              <div className="board-stat-item">
                <span className="bstat-value">{l1Filled}/2</span>
                <span className="bstat-label">Level 1</span>
              </div>
              <div className="board-stat-divider"></div>
              <div className="board-stat-item">
                <span className="bstat-value">{l2Filled}/4</span>
                <span className="bstat-label">Level 2</span>
              </div>
            </div>
          </div>

          {/* Cycles card */}
          <div className="cycles-card glass-card animate-in" style={{animationDelay:'0.15s'}}>
            <div className="cycles-header">
              <div className="cycles-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0118.8-4.3M22 12.5a10 10 0 01-18.8 4.2"/></svg>
                <span>Reinvest Cycles</span>
              </div>
              <span className="cycles-count">{cycles}</span>
            </div>
            {parseInt(cycles) > 0 && (
              <button className="btn-history" onClick={() => loadHistory(selectedPkg)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                View Cycle History
              </button>
            )}
          </div>

        </>
      ) : (
        <div className="empty-state">
          <p>No board data found</p>
        </div>
      )}
    </div>
  );
}
